import React, { useEffect, useRef, useState } from 'react';

import styles from './actSlotAdd.module.css';

import { ReactComponent as Chevron } from '../../../../assets/icons/trvto_downArrow.svg';
import { SingleDatePicker } from 'react-dates';
import moment from 'moment';

import ExistingSlots from '../../../../components/addSlots/ExistingSlots';
import CustomSlotForm from '../../../../components/addSlots/CustomSlotForm';
import AddedSlots from '../../../../components/addSlots/AddedSlots';

import useSlotManager from '../../../../components/addSlots/useSlotManager';
import { toast } from 'react-toastify';
import { supAddTimeSlot } from '../../../../api/supportAdmin';

// ============================================================
// MAIN COMPONENT
// ============================================================

const OrgSlotTab = ({ data, type, addTimeSlot, refresh, isExpanded, setIsExpanded }) => {
    const isMobile = window.innerWidth < 768;
    const bookingTabRef = useRef(null);
    const [selectedDate, setSelectedDate] = useState('');
    const [selectedDateFocused, setSelectedDateFocused] = useState('');

    const [openTime, setOpenTime] = useState(data?.openTime || '');

    const [closeTime, setCloseTime] = useState(data?.closeTime || '');

    // ========================================================
    // SLOT MANAGER
    // ========================================================

    const {
        displaySlots,
        slotExists,
        selectedTemplateKeys,
        existingDateSlots,
        customStart,
        customEnd,
        customTotal,

        setCustomStart,
        setCustomEnd,
        setCustomTotal,

        handleStartTimeChange,
        setAddedSlots,
        addedSlots,
        setExistingDateSlots,
        editingSlotIndex,

        slotError,
        setSelectedTemplateKeys,
        handleToggleTemplate,
        handleAddCustomSlot,
        handleEditSlot,
        handleRemoveSlot,
        clearCustomSlotForm
    } = useSlotManager({
        data,
        openTime,
        closeTime,
        selectedDate,
        type
    });

    // ========================================================
    // SUBMIT STATE
    // ========================================================

    const [submitError, setSubmitError] = useState('');

    const [submitting, setSubmitting] = useState(false);

    const toggleExpand = () => {
        setIsExpanded(!isExpanded);
    };

    useEffect(() => {
        if (isExpanded && bookingTabRef.current) {
            window.scrollTo({
                top: bookingTabRef.current.offsetTop - 160,
                behavior: 'smooth'
            });
        }
    }, [isExpanded]);

    // ========================================================
    // UPDATE OPERATING HOURS
    // ========================================================

    useEffect(() => {
        if (data?.openTime) {
            setOpenTime(data.openTime);
        }

        if (data?.closeTime) {
            setCloseTime(data.closeTime);
        }
    }, [data?.openTime, data?.closeTime]);

    // ========================================================
    // DATE CHANGE
    // ========================================================

    const handleDateChange = (date) => {
        setSubmitError('');

        setSelectedDate(date);
    };

    // ========================================================
    // SUBMIT DATE
    // ========================================================

    const handleSubmitDate = async () => {
        setSubmitError('');
        const startTime = '06:00 AM';
        if (!selectedDate) {
            toast.error('Please select a date.');
            setSubmitError('Please select a date.');
            return;
        }

        if (addedSlots.length === 0) {
            toast.error('Add at least one time slot for this date.');
            setSubmitError('Add at least one time slot for this date.');
            return;
        }

        setSubmitting(true);

        try {
            const startDateTime = selectedDate.set({
                hour: startTime.split(':')[0],
                minute: 0,
                second: 0,
                millisecond: 0
            });
            const dateTime = selectedDate.clone().startOf('day').toISOString();

            const addSlots = await addTimeSlot(data._id, type, {
                date: selectedDate.format('YYYY-MM-DD'),
                slots: addedSlots,
                bookingEnd: selectedDate.format('YYYY-MM-DD')
            });

            toast.success(addSlots.data.message);
            refresh();
            setSelectedTemplateKeys(new Set());
            setSelectedDate(null);
            setAddedSlots([]);
            setExistingDateSlots([]);
            clearCustomSlotForm();

            setSubmitError('');
        } catch (err) {
            const message = err?.response?.data?.message || 'Could not add this date. Please try again.';

            toast.error(message);
            setSubmitError(message);
        } finally {
            setSubmitting(false);
        }
    };
    // ========================================================
    // RENDER
    // ========================================================
    return (
        <div ref={bookingTabRef} className={`tripBookingTab ${isExpanded ? 'expanded' : 'collapsed'}`}>
            {/* ==================================================
                HEADER
            ================================================== */}

            <div className='orgTrDaTabPriceDiv'>
                <div className='flexColumn' style={{ paddingTop: '12px' }}>
                    <span className='orgTrDaTabPrice'>Rs. {data.price?.[0]?.actual}/person</span>

                    <span className='orgTrDaTabIncu'>Excluding all taxes</span>
                </div>

                <div className='tripBookingChevronWrapper'>
                    <button type='button' onClick={toggleExpand} className='collapseArrow'>
                        <Chevron width={24} height={24} />
                    </button>

                    {!isExpanded && isMobile && (
                        <button type='button' className='dateAddBtn blueBtn' onClick={toggleExpand}>
                            Add Activity Date
                        </button>
                    )}
                </div>
            </div>

            {/* ==================================================
                CONTENT
            ================================================== */}

            <div className='tripBookingSubTab'>
                {/* ==================================================
                    DATE
                ================================================== */}

                <div className={styles.formSection}>
                    <label className={styles.sectionTitle}>Select Date</label>

                    <div className={styles.datePickerWrapper}>
                        <SingleDatePicker
                            withPortal={isMobile}
                            date={selectedDate}
                            onDateChange={handleDateChange}
                            displayFormat='DD-MM-YYYY'
                            focused={selectedDateFocused}
                            onFocusChange={({ focused }) => setSelectedDateFocused(focused)}
                            numberOfMonths={1}
                            isOutsideRange={(day) => day.isBefore(moment(), 'day')}
                            showDefaultInputIcon
                            readOnly
                        />
                    </div>
                </div>

                {/* ==================================================
                    OPERATING HOURS
                ================================================== */}

                {(openTime || closeTime) && (
                    <div className={styles.operatingHoursNote}>
                        <span className={styles.operatingHoursIcon}>◷</span>

                        <span>
                            Operating hours: <strong>{openTime || '—'}</strong> to <strong>{closeTime || '—'}</strong>
                        </span>
                    </div>
                )}

                {/* ==================================================
                    EXISTING SLOTS
                ================================================== */}

                <ExistingSlots
                    templates={displaySlots}
                    existingDateSlots={existingDateSlots}
                    selectedTemplateKeys={selectedTemplateKeys}
                    onToggle={handleToggleTemplate}
                    slotExists={slotExists}
                />
                <AddedSlots slots={addedSlots} onEdit={handleEditSlot} onRemove={handleRemoveSlot} />

                {/* ==================================================
                    OR DIVIDER
                ================================================== */}

                {displaySlots.length > 0 && (
                    <div className={styles.orDivider}>
                        <span />

                        <strong>OR</strong>

                        <span />
                    </div>
                )}

                {/* ==================================================
                    CUSTOM SLOT
                ================================================== */}

                <CustomSlotForm
                    customStart={customStart}
                    customEnd={customEnd}
                    customTotal={customTotal}
                    setCustomStart={setCustomStart}
                    setCustomEnd={setCustomEnd}
                    setCustomTotal={setCustomTotal}
                    onStartTimeChange={handleStartTimeChange}
                    onAdd={handleAddCustomSlot}
                    onCancel={clearCustomSlotForm}
                    editing={editingSlotIndex !== null}
                />

                {/* ==================================================
                    SLOT ERROR
                ================================================== */}

                {/* {slotError && <div className={styles.slotErrorText}>{slotError}</div>} */}

                {/* ==================================================
                    ADDED SLOTS
                ================================================== */}

                {/* ==================================================
                    TIP
                ================================================== */}

                {addedSlots.length > 0 && (
                    <div className={styles.slotTip}>
                        <span>💡</span>

                        <span>You can add multiple slots for the same date.</span>
                    </div>
                )}

                {/* ==================================================
                    SUBMIT ERROR
                ================================================== */}

                {/* {submitError && <div className={styles.slotErrorText}>{submitError}</div>} */}

                {/* ==================================================
                    SUBMIT BUTTON
                ================================================== */}

                <button
                    type='button'
                    className={`${styles.addActivityDateBtn} dateAddBtn blueBtn`}
                    onClick={handleSubmitDate}
                    disabled={submitting}
                >
                    {submitting ? 'Adding...' : 'Add Activity Date'}
                </button>
            </div>
        </div>
    );
};

export default OrgSlotTab;
